/**
 * Tab Teams che dà accesso al sito istituzionale e mostra l'identità SSO
 * dell'utente (token Entra validato dal backend, profilo Graph via OBO).
 *
 * SharePoint Online non può essere incapsulato in un iframe esterno
 * (la pagina di login Microsoft non rende dentro un frame), quindi la tab
 * non fa embedding: offre l'apertura del sito, e il manifest dichiara il sito
 * come `websiteUrl` così il pulsante "Apri nel browser" di Teams porta dritto
 * alla destinazione.
 */
import { app } from '@microsoft/teams-js';
import { initTeams, type TeamsClient } from './teams.ts';
import { loadSiteConfig } from './lib/site.ts';
import { apiBase, apiGet, type Identity, type ProfilePayload } from './lib/sso.ts';
import './styles.css';

const config = loadSiteConfig();
const base = apiBase(import.meta.env as unknown as Record<string, string | undefined>);
// Con VITE_DEMO_SSO=1 la tab, anche fuori da Teams, chiama l'API senza token:
// serve a vedere la UI con i dati fittizi del backend in modalità mock.
const demoMode = (import.meta.env.VITE_DEMO_SSO as string | undefined) === '1';
// Con VITE_SSO_ENABLED=1 la pagina mostra il riquadro identità e chiama il
// backend. Nella "pagina di appoggio" (solo link al sito) sta spento.
const ssoEnabled = (import.meta.env.VITE_SSO_ENABLED as string | undefined) === '1';

const el = {
  name: document.getElementById('site-name') as HTMLElement,
  url: document.getElementById('site-url') as HTMLElement,
  openButton: document.getElementById('open-button') as HTMLAnchorElement,
  openTab: document.getElementById('open-tab') as HTMLAnchorElement,
  status: document.getElementById('status') as HTMLElement,
  context: document.getElementById('context') as HTMLElement,
  identity: document.getElementById('identity') as HTMLElement,
  identityStatus: document.getElementById('identity-status') as HTMLElement,
  identityDetails: document.getElementById('identity-details') as HTMLElement,
  retry: document.getElementById('identity-retry') as HTMLButtonElement,
};

function applyTheme(theme: string): void {
  document.documentElement.dataset.theme = theme;
}

/** Apre il sito nel browser di sistema, uscendo dal frame di Teams. */
async function openSite(): Promise<void> {
  try {
    await app.openLink(config.url);
    el.status.textContent = 'Sito aperto in una nuova scheda del browser.';
  } catch {
    // Fallback: link diretto, così l'azione resta sempre disponibile.
    window.open(config.url, '_blank', 'noopener');
    el.status.textContent = 'Sito aperto in una nuova scheda del browser.';
  }
}

/** Scrive una voce del riquadro identità; nasconde la riga se il valore è vuoto. */
function setFact(id: string, value: string | null | undefined): void {
  const dd = document.getElementById(id);
  if (!dd) return;
  const text = (value ?? '').trim();
  dd.textContent = text;
  const row = dd.parentElement;
  if (row) row.hidden = text === '';
}

function resetFacts(): void {
  for (const id of ['fact-name', 'fact-upn', 'fact-department', 'fact-job']) setFact(id, '');
  el.identityDetails.hidden = true;
}

/** Chiede il token SSO e interroga il backend (identità + profilo Graph). */
async function loadIdentity(teams: TeamsClient): Promise<void> {
  if (!teams.context.inTeams && !demoMode) {
    el.identityStatus.textContent =
      'Accesso SSO disponibile solo quando l\'app è aperta in Microsoft Teams.';
    return;
  }

  el.retry.hidden = true;
  resetFacts();
  el.identityStatus.textContent = 'Verifica dell\'identità in corso…';

  try {
    const token = teams.context.inTeams ? await teams.getToken() : null;
    const me = await apiGet<Identity>('/api/me', token, { base });

    setFact('fact-name', me.name);
    setFact('fact-upn', me.upn);
    el.identityDetails.hidden = false;
    el.identityStatus.textContent = me.mock
      ? 'Identità simulata: il backend è in modalità mock.'
      : `Accesso eseguito${me.name ? ` come ${me.name}` : ''}.`;

    try {
      const profile = await apiGet<ProfilePayload>('/api/graph/profile', token, { base });
      setFact('fact-department', profile.profile.department);
      setFact('fact-job', profile.profile.jobTitle);
    } catch (err) {
      // Il profilo Graph è un extra: se il consenso manca, l'identità resta valida.
      console.warn('[sso] profilo Graph non disponibile:', err);
    }
  } catch (err) {
    el.identityStatus.textContent = `Accesso non riuscito: ${(err as Error).message}`;
    el.retry.hidden = false;
  }
}

async function main(): Promise<void> {
  const teams = await initTeams();
  applyTheme(teams.context.theme);
  teams.onThemeChange(applyTheme);

  el.name.textContent = config.name;
  el.url.textContent = config.url;
  el.openButton.href = config.url;
  el.openTab.href = config.url;

  el.openButton.addEventListener('click', (ev) => {
    // In Teams il deep link gestisce l'apertura fuori dal frame.
    if (teams.context.inTeams) {
      ev.preventDefault();
      void openSite();
    }
  });

  const bits: string[] = [];
  if (teams.context.userDisplayName) bits.push(teams.context.userDisplayName);
  if (teams.context.teamDisplayName) bits.push(`Team: ${teams.context.teamDisplayName}`);
  el.context.textContent = bits.join(' · ');

  if (!teams.context.inTeams) {
    el.status.textContent = 'Modalità browser: il sito si apre in una nuova scheda.';
  }

  el.retry.addEventListener('click', () => {
    void loadIdentity(teams);
  });

  if (!ssoEnabled) {
    // "Pagina di appoggio": niente SSO, niente chiamate al backend.
    el.identity.hidden = true;
    return;
  }

  el.identity.hidden = false;
  await loadIdentity(teams);
}

void main();
